import React, {
  useEffect,
  useState,
  useMemo,
  useContext,
  useCallback,
} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {connect} from 'react-redux';
import CreatableSelect from 'react-select/creatable';
import {
  PrimaryButton,
  SettingsToggle,
  TertiaryButton,
  MultiValueRemove,
} from '../../../../components';
import {copyWhite, lock, profileIc} from '../../../../assets/images/images';
import Layout from '../Layout';
import * as Actions from '../../../../store/actions';
import {USE_CASES} from '../CreateFlowModal';
import {toast} from 'react-toastify';
import DeleteFlowModal from './DeleteFlowModal';
import {PageContext} from '../../../../helpers/context';
import {useMutation, useQuery, useQueryClient} from 'react-query';
import {
  deleteFlow,
  fetchFlow,
  generateWebhookUrl,
  updateFlow,
} from '../../../../requests/queries/flows';

function FlowsSettings() {
  const navigate = useNavigate();
  const {slug} = useParams();
  const [openDeleteFlowModal, setOpenDeleteFlowModal] = useState(false);
  const {data: flowData, isLoading} = useQuery(['e-flow', slug], () =>
    fetchFlow(slug),
  );
  const flow = useMemo(
    () => !isLoading && flowData && {flow: flowData?.entity},
    [flowData, isLoading],
  );

  const queryClient = useQueryClient();

  const {mutate: updateCurrentFLow, isLoading: updateLoading} = useMutation(
    'update-flow',
    updateFlow,
  );
  const {mutateAsync: generateWebhookMutation} = useMutation(
    'generate-webhook',
    generateWebhookUrl,
  );
  const {mutate: deleteCurrentFlow, isLoading: deleteLoading} = useMutation(
    'delete-flow',
    deleteFlow,
  );

  const [inputValue, setInputValue] = useState('');
  const [updateData, setUpdate] = useState({
    name: '',
    desc: '',
    webhook: '',
    notification_emails: [],
    receive_webhook: false,
  });

  const {setPageName} = useContext(PageContext);
  useEffect(() => {
    setPageName('easydetect');
  }, [setPageName]);

  useEffect(() => {
    if (flow?.flow) {
      setUpdate({
        name: flow.flow.name || '',
        desc: flow.flow.desc || '',
        webhook: flow.flow.webhook || '',
        notification_emails: flow.flow.notification_emails || [],
        receive_webhook: flow.flow.notification_emails?.length > 0,
      });
    }
  }, [flow]);

  // const ingestBaseUrl =
  //   process.env.REACT_APP_APP_ENV_MODE === 'production'
  //     ? 'https://ingest.dojah.io'
  //     : process.env.REACT_APP_APP_ENV_MODE === 'staging'
  //     ? 'https://staging-treasury.dojah.services'
  //     : 'https://dev-treasury.dojah.services';

  const realTimeUrl = useMemo(
    () => `${flow?.flow?.key}` || '-',
    [flow?.flow?.key],
  );
  const backFillUrl = useMemo(
    () => `${flow?.flow?.backfill_key}` || '-',
    [flow?.flow?.backfill_key],
  );

  const handleChange = e => {
    const {name, value} = e.target;
    setUpdate(prev => ({...prev, [name]: value}));
  };

  const validateEmail = email => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  const handleKeyDown = event => {
    if (!inputValue) return;
    if (['Enter', 'Tab'].includes(event.key)) {
      event.preventDefault();
      if (!validateEmail(inputValue)) {
        toast.error('Invalid email address');
        return;
      }
      if (updateData.notification_emails.includes(inputValue)) {
        toast.error('Email already added');
        return;
      }
      if (updateData.notification_emails.length >= 5) {
        toast.error('Maximum of 5 emails allowed');
        return;
      }

      setUpdate(prev => ({
        ...prev,
        notification_emails: [...prev.notification_emails, inputValue],
      }));
      setInputValue('');
    }
  };

  const handleToggleChange = enabled => {
    setUpdate(prev => ({
      ...prev,
      receive_webhook: enabled,
    }));
  };

  const handleUpdate = useCallback(async () => {
    try {
      const payload = {
        name: updateData.name,
        webhook: updateData.webhook || '-',
        notification_emails: updateData.receive_webhook
          ? updateData.notification_emails
          : [],
        c_webhook: flow?.flow?.c_webhook || '-',
        desc: updateData.desc,
      };

      const webhookChanged = updateData.webhook !== flow?.flow?.webhook;
      if (updateData.webhook && webhookChanged) {
        await generateWebhookMutation({
          webhook_url: updateData.webhook,
          project_id: slug,
          flow_name: updateData.name,
        });
      }

      await updateCurrentFLow(
        {id: slug, data: payload},
        {
          onSuccess: () => {
            queryClient.invalidateQueries(['e-flow', slug]);
            toast.success('Flow updated successfully');
          },
          onError: error => {
            toast.error(error?.response?.data?.error || 'Update failed');
          },
        },
      );
    } catch (error) {
      toast.error(error?.response?.data?.error || 'An error occurred');
    }
  }, [
    updateData,
    flow,
    slug,
    generateWebhookMutation,
    updateCurrentFLow,
    queryClient,
  ]);

  const handleDelete = useCallback(async () => {
    try {
      await deleteCurrentFlow(slug, {
        onSuccess: () => {
          setOpenDeleteFlowModal(false);
          navigate('/flows');
          queryClient.invalidateQueries();
        },
        onError: error => {
          toast.error(error?.response?.data?.error);
        },
      });
    } catch (e) {
      console.error(e);
    }
  }, [deleteCurrentFlow, navigate, queryClient, slug]);

  const existingUseCase = useMemo(
    () => USE_CASES.find(_case => _case.value === flow?.flow?.usecase),
    [flow],
  );

  const copyText = text => {
    navigator.clipboard.writeText(text);
    toast.success('Link copied');
  };

  return (
    <Layout pageTitle="Settings" pageIcon={profileIc}>
      <DeleteFlowModal
        open={openDeleteFlowModal}
        setOpen={setOpenDeleteFlowModal}
        onClick={handleDelete}
        loading={deleteLoading}
      />
      <section className="py-3">
        <form className="flex flex-col w-full gap-6 md:flex-row md:items-start">
          <div className="md:w-1/2">
            <div className="bg-white shadow-cards w-full h-full rounded-xl border-[0.5px] border-[#EFEFEF]">
              <div className="bg-[#F9F9F9] py-3 px-6 rounded-t-xl">
                <p className="font-medium text-sm xl:text-base text-deepBlue">
                  Edit Flow
                </p>
              </div>

              <div className="p-6">
                <label htmlFor="flow_name">
                  Flow name
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={updateData.name}
                    className="mt-2"
                    onChange={handleChange}
                  />
                </label>
                <label htmlFor="flow_use_cases" className="mt-6 relative">
                  Use cases
                  <input
                    type="text"
                    id="use_case"
                    name="use_case"
                    className="mt-2"
                    defaultValue={existingUseCase?.label}
                    disabled
                  />
                  <img
                    src={lock}
                    alt=""
                    width={16}
                    height={16}
                    className="absolute bottom-4 right-4"
                    title="You can't edit this field"
                  />
                </label>
                <label htmlFor="flow_description" className="mt-6 relative">
                  <p>
                    Description <span className="text-grey30">(optional)</span>
                  </p>
                  <textarea
                    name="desc"
                    className="mt-2 min-h-[100px] h-full"
                    value={updateData.desc}
                    onChange={handleChange}
                  />
                </label>
              </div>
            </div>

            <div className="bg-white mt-10 shadow-cards w-full h-full rounded-xl border-[0.5px] border-[#EFEFEF]">
              <div className="bg-[#F9F9F9] py-3 px-6 rounded-t-xl">
                <p className="font-medium text-sm xl:text-base text-deepBlue">
                  Configurations
                </p>
              </div>

              <div className="p-6">
                <label htmlFor="realtime_url" className="relative">
                  Realtime Key
                  <input
                    type="text"
                    id="realtime_url"
                    name="realtime_url"
                    placeholder={isLoading ? '...' : realTimeUrl}
                    className="mt-2"
                    disabled
                  />
                  <button
                    type="button"
                    onClick={() => copyText(realTimeUrl)}
                    className="absolute flex items-center gap-2 p-2 rounded bottom-[9px] right-4 bg-brandBlue"
                  >
                    <img src={copyWhite} alt="" width={14} height={14} />
                    <span className="text-xs font-medium text-white">Copy</span>
                  </button>
                </label>
                <label htmlFor="realtime_url" className="relative mt-6">
                  BackFill Key
                  <input
                    type="text"
                    id="realtime_url"
                    name="realtime_url"
                    placeholder={isLoading ? '...' : backFillUrl}
                    className="mt-2"
                    disabled
                  />
                  <button
                    type="button"
                    onClick={() => copyText(backFillUrl)}
                    className="absolute flex items-center gap-2 p-2 rounded bottom-[9px] right-4 bg-brandBlue"
                  >
                    <img src={copyWhite} alt="" width={14} height={14} />
                    <span className="text-xs font-medium text-white">Copy</span>
                  </button>
                </label>
              </div>
            </div>
          </div>

          <div className="md:w-1/2">
            <div className="bg-white shadow-cards w-full h-full rounded-xl border-[0.5px] border-[#EFEFEF]">
              <div className="bg-[#F9F9F9] py-3 px-6 rounded-t-xl">
                <p className="font-medium text-sm xl:text-base text-deepBlue">
                  Webhook Info
                </p>
              </div>

              <div className="p-6 bg-white rounded-b-xl">
                <label htmlFor="webhook">
                  WEBHOOK URL
                  <input
                    type="text"
                    id="webhook"
                    name="webhook"
                    value={updateData.webhook}
                    className="mt-2"
                    onChange={handleChange}
                    placeholder="Enter webhook URL"
                  />
                </label>
              </div>
            </div>

            <div className="bg-white mt-10 shadow-cards w-full h-full rounded-xl border-[0.5px] border-[#EFEFEF]">
              <div className="bg-[#F9F9F9] py-3 px-6 rounded-t-xl">
                <p className="font-medium text-sm xl:text-base text-deepBlue">
                  Email Notifications
                </p>
              </div>

              <div className="p-6">
                <label htmlFor="receive_webhook">
                  Opt to receive email notifications whenever a new case emerges
                  <div className="mt-4 flex items-center gap-2 p-4 rounded bg-white80 w-full">
                    <SettingsToggle
                      title="receive_webhook"
                      isChecked={updateData.receive_webhook}
                      onCheck={handleToggleChange}
                    />
                    <p className="text-grey text-sm -mb-1">Email</p>
                  </div>
                </label>
                {updateData.receive_webhook && (
                  <>
                    <label htmlFor="notification_emails" className="mt-4 mb-1">
                      Add email address
                    </label>
                    <CreatableSelect
                      components={{
                        DropdownIndicator: null,
                        MultiValueRemove,
                      }}
                      inputValue={inputValue}
                      isClearable
                      isMulti
                      menuIsOpen={false}
                      onChange={newValue => {
                        setUpdate(prev => ({
                          ...prev,
                          notification_emails: newValue
                            ? newValue.map(v => v.value)
                            : [],
                        }));
                      }}
                      onInputChange={setInputValue}
                      onBlur={() => {
                        if (inputValue && validateEmail(inputValue)) {
                          if (
                            !updateData.notification_emails.includes(inputValue)
                          ) {
                            setUpdate(prev => ({
                              ...prev,
                              notification_emails: [
                                ...prev.notification_emails,
                                inputValue,
                              ],
                            }));
                            setInputValue('');
                          }
                        }
                      }}
                      onKeyDown={handleKeyDown}
                      placeholder="Type emails here and press enter or tab to add"
                      value={updateData.notification_emails.map(v => ({
                        label: v,
                        value: v,
                      }))}
                      className="creatable-multi-select rounded"
                      classNamePrefix="react-select"
                    />
                  </>
                )}
              </div>
            </div>

            <div className="flex items-center gap-4 mt-8">
              <PrimaryButton
                loading={updateLoading}
                onClick={handleUpdate}
                buttonText="Save changes"
                className="!h-10"
              />
              <TertiaryButton
                className="!h-10 rounded-lg"
                buttonText="Delete flow"
                onClick={() => setOpenDeleteFlowModal(true)}
              />
            </div>
          </div>
        </form>
      </section>
    </Layout>
  );
}

export default connect(state => state, Actions)(FlowsSettings);
